import { forwardRef, type ReactNode } from "react";
import { type InputProps, TextField } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import classNames from "classnames";
import { C_ERROR, C_INFORMATION, C_ON_SURFACE_VARIANT, C_PRIMARY } from "../icon/Colors";
import { FontSize, getFontStyle } from "./Fonts";

export interface MaterialInputProps extends InputProps {
  className?: string;
  label?: ReactNode;
  helperLabel?: ReactNode;
  shrink?: boolean;
  maxLength?: number;
  name?: string;
  id?: string;
  type?: string;
  defaultValue?: string;
  colorHelperOnError?: boolean;
  manualHeight?: number;
  greenHelperText?: boolean;
  isHighlight?: boolean;
  "data-test-id"?: string;
  autoCompleteOff?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  variant?: string;
}

export const INPUT_FONT = getFontStyle(FontSize.BodyLarge);

const useStyles = makeStyles(() =>
  createStyles({
    helperLabel: {
      ...getFontStyle(FontSize.LabelLarge),
      minHeight: getFontStyle(FontSize.LabelLarge).lineHeight,
      marginTop: "4px",
    },
    helperLabelColor: {
      color: `${C_ON_SURFACE_VARIANT} !important`,
    },
    greenHelperLabelColor: {
      color: `${C_PRIMARY} !important`,
    },
    cssError: {
      color: `${C_ERROR} !important`,
    },
    cssInfo: {
      color: `${C_INFORMATION} !important`,
    },
  }),
);

export const MaterialInput = forwardRef<HTMLInputElement, MaterialInputProps>((props, ref) => {
  const {
    value,
    shrink,
    helperLabel = "",
    maxLength,
    manualHeight,
    className,
    colorHelperOnError = true,
    greenHelperText = false,
    isHighlight = false,
    "data-test-id": testId,
    autoCompleteOff,
    autoComplete,
    startAdornment,
    endAdornment,
    inputProps,
    ...rest
  } = props;

  const classes = useStyles();

  return (
    <TextField
      fullWidth
      value={value as string}
      helperText={helperLabel}
      inputRef={ref}
      autoComplete={autoComplete ? autoComplete : autoCompleteOff ? "off" : "on"}
      className={className}
      rows={manualHeight}
      slotProps={{
        input: {
          startAdornment: startAdornment,
          endAdornment: endAdornment,
        },
        inputLabel: {
          shrink,
          root: classNames(isHighlight && classes.cssInfo),
        },
        htmlInput: {
          maxLength,
          ...inputProps,
        },
        formHelperText: {
          classes: {
            root: classNames(greenHelperText ? classes.greenHelperLabelColor : classes.helperLabelColor),
            error: classNames(colorHelperOnError ? classes.cssError : classes.helperLabel),
          },
        },
      }}
      {...rest}
    />
  );
});
